import React from 'react';
import { useBillingBalance } from '@/hooks/api/useBilling';
import { LoadingSpinner, ErrorMessage } from '@/components/ui';

interface BalanceCardProps {
  onAddFunds?: () => void;
  showAddButton?: boolean;
  className?: string;
}

export const BalanceCard: React.FC<BalanceCardProps> = ({
  onAddFunds,
  showAddButton = true,
  className = '',
}) => {
  const { data: balance, isLoading, error, refetch } = useBillingBalance();

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Balance</h2>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Balance</h2>
        <ErrorMessage
          message="Failed to load balance information"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const balanceAmount = Number(balance?.credit_balance ?? balance?.current_balance ?? 0);
  const isLowBalance = balanceAmount < 5;

  // Trial period information
  const trialActive = balance?.trial_active && !balance?.is_admin;
  const trialDaysRemaining = balance?.trial_days_remaining ?? null;
  const isTrialExpiringSoon = trialDaysRemaining !== null && trialDaysRemaining <= 2;

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Balance</h2>

      {/* Trial Period Display */}
      {trialActive && trialDaysRemaining !== null && (
        <div
          className={`mb-4 p-3 rounded-md border ${
            isTrialExpiringSoon
              ? 'bg-orange-50 border-orange-200'
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <p
            className={`text-sm font-medium ${
              isTrialExpiringSoon ? 'text-orange-800' : 'text-blue-800'
            }`}
          >
            {trialDaysRemaining === 0
              ? 'Your free trial expires today'
              : trialDaysRemaining === 1
                ? '1 day remaining in your free trial'
                : `${trialDaysRemaining} days remaining in your free trial`}
          </p>
          {isTrialExpiringSoon && (
            <p className="text-xs text-orange-700 mt-1">
              Add credits to continue using the service after your trial ends.
            </p>
          )}
        </div>
      )}

      <div className="text-center py-4">
        <p
          className={`text-4xl font-bold mb-2 ${
            isLowBalance ? 'text-orange-600' : 'text-gray-900'
          }`}
        >
          {balanceAmount.toFixed(1)} credits
        </p>
        <p className="text-sm text-gray-600 mb-4">Available credits</p>

        {isLowBalance && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <p className="text-sm text-orange-800">
              Your credits are running low. Purchase more credits to continue using the service.
            </p>
          </div>
        )}

        {showAddButton && (
          <button
            onClick={onAddFunds}
            className="w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Add Credits
          </button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Currency:</span>
          <span className="font-medium text-gray-900">{balance?.currency || 'USD'}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-gray-600">Last Updated:</span>
          <span className="font-medium text-gray-900">
            {balance?.last_updated
              ? new Date(balance.last_updated).toLocaleDateString()
              : 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
};
